import { useState, useMemo, useRef } from "react";
import { ArrowRight, X, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";

// Paginazione predisposta per quando i progetti aumenteranno di numero.
// Per ora disattivata (valore alto = mostra sempre tutto in una pagina): abbassare
// questo valore (es. 10) quando i progetti saranno effettivamente molti (richiesta utente #1955).
const PROGETTI_PER_PAGINA = 999;

/* ─────────────────────────────────────────────────────────────────
   DATI UNIFICATI — stato: "in-corso" | "concluso"
───────────────────────────────────────────────────────────────── */
const PROGETTI = [
  /* ── IN CORSO ── */
  {
    nome: "Green Business: il bilancio di sostenibilità per le PMI di Roma e Regione Lazio",
    slug: "green-business",
    img: "/local-assets/xcjhcq5g_CCR-I-2026-I-Camerali-I-Green-Business-I-Banner-Web-1.jpg",
    tag: "Sostenibilità",
    stato: "in-corso",
    internalPath: "/progetti/green-business",
  },
  {
    nome: "Futuro in Rete – Imprese in connessione",
    slug: "futuro-in-rete-imprese-in-connessione",
    img: "/local-assets/uu48uah2_CCR-I-2026-I-Camerali-I-Futuro-in-Rete-I-Banner-Sito.jpg",
    tag: "Digitalizzazione",
    stato: "in-corso",
  },
  {
    nome: "Sapori del Lazio – Passioni giapponesi",
    slug: "sapori-del-lazio-passioni-giapponesi",
    img: "/local-assets/el0gm8s5_Banner-Nuove-Dimensioni.png",
    tag: "Enogastronomia",
    stato: "in-corso",
  },
  {
    nome: "Strategie di ingresso nei mercati internazionali",
    slug: "strategie-mercati-internazionali",
    img: "/local-assets/yd3bi470_ConfCom_Internazionalizzazione_banner-scaled.jpg",
    tag: "Internazionalizzazione",
    stato: "in-corso",
  },
  {
    nome: "Lo sviluppo del retail: prospettive di crescita per Roma Capitale",
    slug: "lo-sviluppo-del-retail",
    img: "/local-assets/6nomm5t3_CONFCOMMERCIO-I-2025-I-Progetti-Camerali-I-Retail-I-Banner-Web.webp",
    tag: "Retail",
    stato: "in-corso",
  },
  {
    nome: "Il Giubileo del Gusto",
    slug: "il-giubileo-del-gusto",
    img: "/local-assets/l739msj3_GIUBILEO-DEL-GUSTO-Banner-donna_removed_page-0001-scaled.webp",
    tag: "Enogastronomia",
    stato: "in-corso",
  },
  {
    nome: "GenerAzione Donna",
    slug: "generazione-donna",
    img: "/local-assets/9bp6m13q_CONFCOMMERCIO-I-2025-I-Progetti-Camerali-I-GeneraZione-Donna-I-Banner-Web-r50uw1t8s117bapcgcq8oc7u97lygrmj6jco7z8fgg.jpg",
    tag: "Empowerment Femminile",
    stato: "in-corso",
  },
  {
    nome: "Tridente Moda District",
    slug: "tridente-moda-district",
    img: "/local-assets/lcnsm53n_CONFCOMMERCIO-I-2025-I-Progetti-Camerali-I-Tridente-Moda-District-I-Banner-Web.webp",
    tag: "Moda",
    stato: "in-corso",
  },
  {
    nome: "Patto Giubileo Per il lavoro",
    slug: "patto-giubileo-per-il-lavoro",
    img: "/local-assets/t6dkv079_CONFCOMMERCIO-I-2025-I-Progetti-Camerali-I-Patto-Giubileo-Lavoro-I-Banner-Web-1.webp",
    tag: "Lavoro e Welfare",
    stato: "in-corso",
  },
  {
    nome: "Splendori del Giubileo: Arte Sacra & Antiquariato",
    slug: "splendori-del-giubileo-arte-sacra-antiquariato",
    img: null,
    tag: "Artigianato",
    stato: "in-corso",
  },
  /* ── IMPEGNO SOCIALE (in-corso) ── */
  {
    nome: "Seconda Chance",
    slug: "seconda-chance",
    img: "/progetto-seconda-chance.webp",
    tag: "Impegno sociale",
    stato: "in-corso",
    desc: "Percorso di reinserimento lavorativo per persone in situazioni di fragilità sociale, in collaborazione con enti del terzo settore e istituzioni locali.",
  },
  {
    nome: "Progetto Calma Sensoriale",
    slug: "progetto-calma-sensoriale",
    img: "/progetto-calma-sensoriale.webp",
    tag: "Impegno sociale",
    stato: "in-corso",
    desc: "Iniziativa per rendere gli spazi commerciali più accessibili e accoglienti per persone con disturbi dello spettro autistico e sensibilità sensoriali.",
  },
  /* ── CONCLUSI con immagine ── */
  {
    nome: "Civitavecchia Excellence",
    slug: "civitavecchia-excellence",
    img: "/local-assets/2ndev7ak_Civitavecchia-Excellence-Logo-1-scaled.jpg",
    tag: "Territorio",
    stato: "concluso",
  },
  {
    nome: "Ostia: Giubileo del mare",
    slug: "giubileo-ostia",
    img: "/local-assets/4e2etkzu_ostia-giubileo-del-mare.png",
    tag: "Territorio",
    stato: "concluso",
  },
  {
    nome: "Rivoluzione Moda: FASHION AI",
    slug: "rivoluzione-moda-fashion-ai",
    img: "/local-assets/mfessiw7_Banner-sito-.webp",
    tags: ["Innovazione", "Digitalizzazione", "Moda"],
    stato: "concluso",
  },
  {
    nome: "Smart Start Up",
    slug: "smart-start-up",
    img: "/local-assets/jx91a0y7_CONFCOMMERCIO-I-2025-I-Progetti-Camerali-I-Smart-Start-Up-I-Banner-Web-1-1.webp",
    tag: "Innovazione",
    stato: "concluso",
  },
  {
    nome: "Gli Amici del Pellegrino",
    slug: "gli-amici-del-pellegrino",
    img: "/local-assets/0a7hvhlo_CONFCOMMERCIO-I-2025-I-Amici-del-Pellegrino-I-Banner-Web.webp",
    tag: "Turismo",
    stato: "concluso",
  },
  {
    nome: "CONFUTURE: Connecting Rome",
    slug: "confuture-connecting-rome",
    img: "/local-assets/ggbc1g1q_CONFUTUREbanner.webp",
    tag: "Innovazione",
    stato: "concluso",
  },
  {
    nome: "EDI 5.0",
    slug: "edi-5-0",
    img: "/local-assets/edi-5-0-banner.png",
    imgFit: "contain",
    tags: ["Innovazione", "Digitalizzazione"],
    stato: "concluso",
    internalPath: "/progetti/edi-5-0",
    desc: "Il Polo di Innovazione Digitale di Confcommercio: 4 assessment gratuiti e consulenza personalizzata per la trasformazione digitale e sostenibile delle PMI italiane.",
  },
  /* ── CONCLUSI solo testo ── */
  { nome: "Botteghe Storiche: ieri, oggi e domani", slug: "botteghe-storiche-ieri-oggi-e-domani", img: null, tag: "Commercio", stato: "concluso" },
  { nome: "Un Gioiello per il Giubileo", slug: "un-gioiello-per-il-giubileo", img: null, tag: "Artigianato", stato: "concluso" },
  { nome: "innovAzione", slug: "_innovazione", img: null, tag: "Innovazione", stato: "concluso" },
  { nome: "Verso il bilancio di sostenibilità ambientale", slug: "verso-il-bilancio-di-sostenibilita-ambientale", img: null, tag: "Sostenibilità", stato: "concluso" },
  { nome: "80 Anni Tour Roadshow", slug: "80-anni-tour-roadshow", img: null, tag: "Territorio", stato: "concluso" },
  { nome: "Preziosare Evolution", slug: "preziosare-evolution", img: null, tag: "Artigianato", stato: "concluso" },
  { nome: "Orientare il lavoro alla parità di genere", slug: "orientare-il-lavoro-alla-parita-di-genere", img: null, tag: "Empowerment Femminile", stato: "concluso" },
  { nome: "ImprendiGreen", slug: "imprendigreen", img: null, tag: "Sostenibilità", stato: "concluso" },
  { nome: "Innovare le reti di orientamento al lavoro", slug: "innovare-le-reti-di-orientamento-al-lavoro", img: null, tag: "Lavoro e Welfare", stato: "concluso" },
  { nome: "#CONDIVIDIAMO2 – Nuovi modelli organizzativi", slug: "condividiamo2", img: null, tag: "Lavoro e Welfare", stato: "concluso" },
  { nome: "Ristorante tipico romanesco", slug: "ristorante-tipico-romanesco", img: null, tag: "Enogastronomia", stato: "concluso" },
];

/* ─────────────────────────────────────────────────────────────────
   COLORI PER STATO (filtro temporale) — verde / grigio
───────────────────────────────────────────────────────────────── */
const STATO_CONFIG = {
  "in-corso": {
    label: "In corso",
    tabActive: "border-emerald-500 text-emerald-700",
    badge: "bg-emerald-500 text-white",
    badgeInactive: "bg-emerald-100 text-emerald-700",
  },
  "concluso": {
    label: "Conclusi",
    tabActive: "border-slate-400 text-slate-600",
    badge: "bg-slate-500 text-white",
    badgeInactive: "bg-slate-100 text-slate-600",
  },
};

/* ─────────────────────────────────────────────────────────────────
   COLORI PER TEMA (filtro argomento) — palette distinta
───────────────────────────────────────────────────────────────── */
const TEMA_COLORS = {
  "Impegno sociale":        { chip: "border-rose-200 text-rose-700 hover:border-rose-400",         active: "bg-rose-600 text-white border-rose-600",         badge: "bg-rose-50 text-rose-700" },
  "Sostenibilità":          { chip: "border-emerald-200 text-emerald-700 hover:border-emerald-400", active: "bg-emerald-600 text-white border-emerald-600",   badge: "bg-emerald-50 text-emerald-700" },
  "Digitalizzazione":       { chip: "border-blue-200 text-blue-700 hover:border-blue-400",          active: "bg-blue-600 text-white border-blue-600",         badge: "bg-blue-50 text-blue-700" },
  "Enogastronomia":         { chip: "border-amber-200 text-amber-700 hover:border-amber-400",       active: "bg-amber-500 text-white border-amber-500",       badge: "bg-amber-50 text-amber-700" },
  "Internazionalizzazione": { chip: "border-indigo-200 text-indigo-700 hover:border-indigo-400",    active: "bg-indigo-600 text-white border-indigo-600",     badge: "bg-indigo-50 text-indigo-700" },
  "Retail":                 { chip: "border-teal-200 text-teal-700 hover:border-teal-400",          active: "bg-teal-600 text-white border-teal-600",         badge: "bg-teal-50 text-teal-700" },
  "Empowerment Femminile":  { chip: "border-pink-200 text-pink-700 hover:border-pink-400",          active: "bg-pink-600 text-white border-pink-600",         badge: "bg-pink-50 text-pink-700" },
  "Lavoro e Welfare":        { chip: "border-slate-200 text-slate-600 hover:border-slate-400",       active: "bg-slate-600 text-white border-slate-600",       badge: "bg-slate-100 text-slate-700" },
  "Moda":                   { chip: "border-purple-200 text-purple-700 hover:border-purple-400",    active: "bg-purple-600 text-white border-purple-600",     badge: "bg-purple-50 text-purple-700" },
  "Artigianato":            { chip: "border-yellow-200 text-yellow-700 hover:border-yellow-400",    active: "bg-yellow-500 text-white border-yellow-500",     badge: "bg-yellow-50 text-yellow-700" },
  "Innovazione":            { chip: "border-cyan-200 text-cyan-700 hover:border-cyan-400",          active: "bg-cyan-600 text-white border-cyan-600",         badge: "bg-cyan-50 text-cyan-700" },
  "Turismo":                { chip: "border-sky-200 text-sky-700 hover:border-sky-400",             active: "bg-sky-600 text-white border-sky-600",           badge: "bg-sky-50 text-sky-700" },
  "Territorio":             { chip: "border-lime-200 text-lime-700 hover:border-lime-400",          active: "bg-lime-600 text-white border-lime-600",         badge: "bg-lime-50 text-lime-700" },
  "Commercio":              { chip: "border-orange-200 text-orange-700 hover:border-orange-400",    active: "bg-orange-500 text-white border-orange-500",     badge: "bg-orange-50 text-orange-700" },
};

/* Restituisce sempre un array di tag: supporta sia `tag` singolo (legacy)
   sia `tags` multipli (es. un progetto che appartiene a più categorie) */
const getTags = (p) => p.tags || (p.tag ? [p.tag] : []);

/* ─────────────────────────────────────────────────────────────────
   CARD con immagine
───────────────────────────────────────────────────────────────── */
function CardRich({ p }) {
  const sc = STATO_CONFIG[p.stato];
  const tags = getTags(p);
  const cls = "group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all";

  const inner = (
    <>
      <div className={`h-52 overflow-hidden ${p.imgFit === "contain" ? "bg-white flex items-center justify-center" : ""}`}>
        <img
          src={p.img}
          alt={p.nome}
          className={`w-full h-full ${p.imgFit === "contain" ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sc.badgeInactive}`}>{sc.label}</span>
          {tags.map((t) => (
            <span key={t} className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${(TEMA_COLORS[t] || { badge: "bg-gray-100 text-gray-600" }).badge}`}>{t}</span>
          ))}
        </div>
        <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors mb-1">{p.nome}</h3>
        {p.desc && <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mt-2 mb-3">{p.desc}</p>}
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--confcom-blue)] mt-2">Scopri il progetto <ArrowRight size={12} aria-hidden="true" /></span>
      </div>
    </>
  );

  if (p.internalPath) {
    return <Link to={p.internalPath} className={cls} data-testid={`progetto-${p.slug}`}>{inner}</Link>;
  }
  return (
    <a href={`https://www.confcommercioroma.it/progetti/${p.slug}/`} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`progetto-${p.slug}`}>
      {inner}
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CARD solo testo
───────────────────────────────────────────────────────────────── */
function CardText({ p }) {
  const tags = getTags(p);
  return (
    <a
      href={`https://www.confcommercioroma.it/progetti/${p.slug}/`}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between bg-white p-4 rounded-xl border border-[var(--confcom-border)] hover:border-[var(--confcom-blue)]/30 hover:shadow-md transition-all"
      data-testid={`progetto-${p.slug}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {tags.map((t) => (
          <span key={t} className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${(TEMA_COLORS[t] || { badge: "bg-gray-100 text-gray-600" }).badge}`}>
            {t}
          </span>
        ))}
        <span className="text-sm font-medium text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] group-hover:text-[var(--confcom-blue)] transition-colors leading-snug truncate">
          {p.nome}
        </span>
      </div>
      <ArrowRight size={16} className="text-[var(--confcom-text-muted)] group-hover:text-[var(--confcom-blue)] shrink-0 ml-3 transition-colors" aria-hidden="true" />
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PAGINA PRINCIPALE
───────────────────────────────────────────────────────────────── */
export default function ProgettiPage() {
  const [statoAttivo, setStatoAttivo] = useState(null);
  const [temaAttivo, setTemaAttivo] = useState(null);
  const [pagina, setPagina] = useState(1);
  const filterRef = useRef(null);
  const scrollToResults = () => {
    if (filterRef.current) {
      window.scrollTo({ top: filterRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  // Match del filtro tema: un progetto può appartenere a più tag (getTags)
  const matchTema = (p, tema) => {
    if (!tema) return true;
    return getTags(p).includes(tema);
  };

  const risultati = useMemo(() => {
    return PROGETTI.filter((p) => {
      if (statoAttivo && p.stato !== statoAttivo) return false;
      if (!matchTema(p, temaAttivo)) return false;
      return true;
    });
  }, [statoAttivo, temaAttivo]);

  const hasFilters = statoAttivo || temaAttivo;

  const totalPagine = Math.max(1, Math.ceil(risultati.length / PROGETTI_PER_PAGINA));
  const risultatiPagina = useMemo(() => {
    const start = (pagina - 1) * PROGETTI_PER_PAGINA;
    return risultati.slice(start, start + PROGETTI_PER_PAGINA);
  }, [risultati, pagina]);

  const cambiaPagina = (nuovaPagina) => {
    if (nuovaPagina < 1 || nuovaPagina > totalPagine) return;
    setPagina(nuovaPagina);
    scrollToResults();
  };

  /* Temi disponibili in base allo stato selezionato */
  const temiDisponibili = useMemo(() => {
    const base = statoAttivo ? PROGETTI.filter((p) => p.stato === statoAttivo) : PROGETTI;
    const set = new Set();
    base.forEach((p) => getTags(p).forEach((t) => set.add(t)));
    return Object.keys(TEMA_COLORS).filter((t) => set.has(t));
  }, [statoAttivo]);

  const richResults = risultatiPagina.filter((p) => p.img);
  const textResults = risultatiPagina.filter((p) => !p.img);

  return (
    <main id="main-content" data-testid="progetti-page">

      {/* ── HERO ── */}
      <section className="relative h-[320px] md:h-[380px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920&q=60&auto=format"
          alt="Team progetto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Iniziative strategiche
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Progetti
          </h1>
          <p className="text-lg text-white/80 mt-4 max-w-3xl">
            Confcommercio Roma sviluppa e promuove progetti strategici per sostenere la crescita, l'innovazione e l'inclusione delle imprese locali.
          </p>
        </div>
      </section>

      {/* ── BARRA FILTRI STICKY ── */}
      <section ref={filterRef} className="sticky top-[104px] lg:top-[168px] z-20 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Riga 1 — FILTRO TEMPORALE (tabs con colori stato) */}
          <div className="flex overflow-x-auto border-b border-[var(--confcom-border)]" style={{ scrollbarWidth: "none" }}>

            {/* Tab "Tutti" */}
            <button
              onClick={() => { setStatoAttivo(null); setTemaAttivo(null); setPagina(1); scrollToResults(); }}
              data-testid="filtro-stato-tutti"
              className={`shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                !statoAttivo
                  ? "border-[var(--confcom-blue)] text-[var(--confcom-blue)]"
                  : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
              }`}
            >
              Tutti
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${!statoAttivo ? "bg-[var(--confcom-blue)] text-white" : "bg-gray-100 text-gray-500"}`}>
                {PROGETTI.length}
              </span>
            </button>

            {/* Tab "In corso" */}
            {["in-corso", "concluso"].map((stato) => {
              const cfg = STATO_CONFIG[stato];
              const count = PROGETTI.filter((p) => p.stato === stato).length;
              const isActive = statoAttivo === stato;
              return (
                <button
                  key={stato}
                  onClick={() => { setStatoAttivo(isActive ? null : stato); setTemaAttivo(null); setPagina(1); scrollToResults(); }}
                  data-testid={`filtro-stato-${stato}`}
                  className={`shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                    isActive
                      ? cfg.tabActive
                      : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
                  }`}
                >
                  {cfg.label}
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? cfg.badge : cfg.badgeInactive}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Riga 2 — FILTRO TEMA (chip colorati) */}
          {temiDisponibili.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 py-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] shrink-0">
                Tema
              </span>
              <button
                onClick={() => { setTemaAttivo(null); setPagina(1); scrollToResults(); }}
                data-testid="filtro-tema-tutti"
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                  !temaAttivo
                    ? "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]"
                    : "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]"
                }`}
              >
                Tutti
              </button>
              {temiDisponibili.map((t) => {
                const tc = TEMA_COLORS[t];
                const base = statoAttivo ? PROGETTI.filter((p) => p.stato === statoAttivo) : PROGETTI;
                const count = base.filter((p) => matchTema(p, t)).length;
                const isActive = temaAttivo === t;
                return (
                  <button
                    key={t}
                    onClick={() => { setTemaAttivo(isActive ? null : t); setPagina(1); scrollToResults(); }}
                    data-testid={`filtro-tema-${t.toLowerCase().replace(/[\s&]/g, "-").slice(0, 25)}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                      isActive ? tc.active : tc.chip
                    }`}
                  >
                    {t}
                    <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${isActive ? "bg-white/25 text-white" : "bg-white/80"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* ── RISULTATI ── */}
      <section className="py-12 bg-[#F8FAFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Counter + reset filtri */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-sm text-[var(--confcom-text-muted)]" data-testid="progetti-count">
              <span className="font-bold text-[var(--confcom-text-primary)]">{risultati.length}</span>{" "}
              progett{risultati.length === 1 ? "o" : "i"} trovat{risultati.length === 1 ? "o" : "i"}
            </p>
            {hasFilters && (
              <button
                onClick={() => { setStatoAttivo(null); setTemaAttivo(null); setPagina(1); scrollToResults(); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--confcom-blue)] hover:underline"
                data-testid="reset-filtri"
              >
                <X size={12} /> Rimuovi filtri
              </button>
            )}
          </div>

          {risultati.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-[var(--confcom-text-muted)] mb-3">Nessun progetto trovato con i filtri selezionati.</p>
              <button
                onClick={() => { setStatoAttivo(null); setTemaAttivo(null); setPagina(1); scrollToResults(); }}
                className="text-sm text-[var(--confcom-blue)] hover:underline"
              >
                Reimposta i filtri
              </button>
            </div>
          ) : (
            <>
              {/* Card con immagine */}
              {richResults.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6 mb-6" data-testid="progetti-rich-grid">
                  {richResults.map((p) => (
                    <CardRich key={p.slug} p={p} />
                  ))}
                </div>
              )}
              {/* Card solo testo */}
              {textResults.length > 0 && (
                <>
                  {richResults.length > 0 && (
                    <div className="flex items-center gap-3 my-6">
                      <div className="flex-1 h-px bg-[var(--confcom-border)]" />
                      <span className="text-xs font-bold uppercase tracking-widest text-[var(--confcom-text-muted)]">
                        Altri progetti
                      </span>
                      <div className="flex-1 h-px bg-[var(--confcom-border)]" />
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-3" data-testid="progetti-text-grid">
                    {textResults.map((p) => (
                      <CardText key={p.slug} p={p} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Paginazione */}
          {totalPagine > 1 && (
            <Pagination className="mt-12" data-testid="progetti-pagination">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => { e.preventDefault(); cambiaPagina(pagina - 1); }}
                    className={pagina === 1 ? "pointer-events-none opacity-40" : ""}
                    data-testid="progetti-pagination-prev"
                  />
                </PaginationItem>
                {Array.from({ length: totalPagine }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPagine || Math.abs(n - pagina) <= 1)
                  .reduce((acc, n, idx, arr) => {
                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push("ellipsis-" + n);
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n) =>
                    typeof n === "string" ? (
                      <PaginationItem key={n}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={n}>
                        <PaginationLink
                          href="#"
                          isActive={n === pagina}
                          onClick={(e) => { e.preventDefault(); cambiaPagina(n); }}
                          data-testid={`progetti-pagination-page-${n}`}
                        >
                          {n}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => { e.preventDefault(); cambiaPagina(pagina + 1); }}
                    className={pagina === totalPagine ? "pointer-events-none opacity-40" : ""}
                    data-testid="progetti-pagination-next"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}

        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="progetti-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:progetti@confcommercioroma.it" className="hover:text-white transition-colors">progetti@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437605" className="hover:text-white transition-colors">Tel: 06 68437 605-353-625</a>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}
